import { useEffect, useState } from 'react';
import { getSubmittedFormDetails } from '../api/getSubmittedFormDetailsApiService';
import type { SubmittedFormDetails } from '../types';
import { VIEW_SUBMITTED_FORM_LABELS } from '../constants/labels';

export interface UseViewSubmittedFormResult {
  data: SubmittedFormDetails | null;
  isLoading: boolean;
  error: string | null;
  notFound: boolean;
  forbidden: boolean;
  clientSignatureUrl: string | null;
  techSignatureUrl: string | null;
  attachmentUrls: Record<string, string>;
}

export function useViewSubmittedForm(id: string): UseViewSubmittedFormResult {
  const [data, setData] = useState<SubmittedFormDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [clientSignatureUrl, setClientSignatureUrl] = useState<string | null>(
    null
  );
  const [techSignatureUrl, setTechSignatureUrl] = useState<string | null>(null);
  const attachmentUrls: Record<string, string> = {};

  useEffect(() => {
    let mounted = true;

    async function load() {
      setIsLoading(true);
      setError(null);
      setNotFound(false);
      setForbidden(false);

      const result = await getSubmittedFormDetails(id);

      if (!mounted) {
        return;
      }

      if (!result.success) {
        if (result.notFound) {
          setNotFound(true);
        } else if (result.forbidden) {
          setForbidden(true);
        } else {
          setError(result.error ?? VIEW_SUBMITTED_FORM_LABELS.LOADING_ERROR);
        }
        setIsLoading(false);
        return;
      }

      if (!result.data) {
        return;
      }
      const formData = result.data;
      setData(formData);

      // Signatures include presigned GET URLs directly from the API response
      if (formData.task_form.client_signature?.url) {
        setClientSignatureUrl(formData.task_form.client_signature.url);
      }
      if (formData.task_form.technician_signature?.url) {
        setTechSignatureUrl(formData.task_form.technician_signature.url);
      }

      if (mounted) {
        setIsLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [id]);

  return {
    data,
    isLoading,
    error,
    notFound,
    forbidden,
    clientSignatureUrl,
    techSignatureUrl,
    attachmentUrls,
  };
}
